import { Fragment, useEffect, useState } from 'react';
import { Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from '@mui/material';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { TextBlock } from '../../Atoms/Text';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { createRunTotalAudit } from '../../../store/RunTotalAudit';

export interface NewRunTotalAuditDialogParams {
  open: boolean;
  setOpen: (state: boolean) => void;
  onSuccess: () => void;
}

export const NewRunTotalAuditDialog = ({ open, setOpen, onSuccess }: NewRunTotalAuditDialogParams) => {
  const { setSnack } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [startBeforeSnapshot, setStartBeforeSnapshot] = useState(true);

  const handleClose = () => {
    if (!isSubmitting) setOpen(false);
  };

  const handleSubmitForm = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    const { data, error } = await createRunTotalAudit(startBeforeSnapshot);
    setIsSubmitting(false);
    if (data) {
      setSnack({ message: 'Run Totals Audit was created successfully', type: 'success', open: true });
      setOpen(false);
      onSuccess();
    } else if (error) {
      setSnack({
        message: `There was an error creating run totals audit: \n${error.message}`,
        type: 'error',
        open: true
      });
    }
  };

  useEffect(() => {
    setStartBeforeSnapshot(true);
  }, [open]);

  return (
    <Dialog
      data-testid="new-run-total-audit-dialog"
      open={open}
      onClose={handleClose}>
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          New Run Totals Audit
        </TextBlock>
      </DialogTitle>
      <Fragment>
        <DialogContent sx={{ minHeight: 70, width: '27rem' }}>
          <div className="space-y-3 flex">
            <FormControlLabel
              control={
                <Checkbox
                  checked={startBeforeSnapshot}
                  onChange={(e) => setStartBeforeSnapshot(e.target.checked)}
                  name="startBeforeSnapshot"
                  data-testid="start-before-snapshot-checkbox"
                />
              }
              label="Start Before snapshot on creation"
            />
          </div>
        </DialogContent>
        <hr className="mb-1 mx-6" />
        <DialogActions sx={{ marginRight: 2, pb: 2 }}>
          <button
            className={DEFAULT_CANCEL_BUTTON_STYLE}
            onClick={handleClose}>
            Cancel
          </button>
          {isSubmitting ? (
            <div className="flex">
              <ComponentSpinner
                sx={{ mt: '0', m: '0' }}
                label="Creating..."
              />
            </div>
          ) : (
            <button
              data-testid="submit-new-run-total-audit"
              disabled={isSubmitting}
              onClick={handleSubmitForm}
              className={DEFAULT_ADVANCE_BUTTON_STYLE}
              type="submit">
              Save
            </button>
          )}
        </DialogActions>
      </Fragment>
    </Dialog>
  );
};
